/**
 * Authentication Service
 *
 * Orchestrates the authentication flow:
 * 1. Login with credentials
 * 2. Store auth data in AuthStore
 * 3. Initialize session (projects, templates)
 * 4. Navigate to appropriate route
 *
 * Responsibilities:
 * - Handle login/logout flow
 * - Coordinate with SessionInitService for data loading
 * - Manage navigation after authentication
 * - Provide clean error handling for UI
 *
 * Architecture:
 * - Uses signals for reactive state
 * - Delegates session initialization to SessionInitService
 * - Integrates with AuthStore for auth state
 * - Returns Observable for component subscription
 */

import { Injectable, inject, ApplicationRef, createComponent, EnvironmentInjector } from '@angular/core';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import { Router } from '@angular/router';
import { Observable, from, of } from 'rxjs';
import { map, catchError, switchMap, tap, finalize } from 'rxjs';
import { environment } from '@environ/environment';
import { AuthStore } from '../stores/auth-store';
import { SessionInitService } from './session-init.service';
import { ProjectsService } from '@app/core/services/projects.service';
import { DownloadsService } from '@app/core/services/downloads.service';
import { OttoIntegrationService } from '@app/core/services/otto-integration.service';
import { LoginCredentials, LoginResponse, LoginResult } from '../models/auth.model';
import { devLog, devError, devWarn } from '@app/core/utils/dev-logger';
import { LoadingSplashComponent } from '@app/shared/components/organisms/loading-splash/loading-splash.component';

@Injectable({ providedIn: 'root' })
export class AuthService {
  private http = inject(HttpClient);
  private router = inject(Router);
  private authStore = inject(AuthStore);
  private sessionInit = inject(SessionInitService);
  private projectsService = inject(ProjectsService);
  private downloadsService = inject(DownloadsService);
  private ottoService = inject(OttoIntegrationService);
  private appRef = inject(ApplicationRef);
  private injector = inject(EnvironmentInjector);

  private baseUrl = environment.apiUrl;

  // Splash component reference for showing/hiding during auth flow
  private splashComponentRef: ReturnType<typeof createComponent<LoadingSplashComponent>> | null = null;

  /**
   * Login with credentials
   *
   * Flow:
   * 1. Show loading splash
   * 2. POST credentials to API (0-15%)
   * 3. Store auth data in AuthStore (15-30%)
   * 4. Initialize session - load projects, templates (30-70%)
   * 5. Ensure correct project is active (70-85%)
   * 6. Navigate to returnUrl or default route (85-100%)
   * 7. Hide splash
   *
   * Note: Taxonomies and notifications load in background (non-blocking)
   *
   * @param credentials - Username and password
   * @param returnUrl - Optional URL to redirect after login
   * @returns Observable<LoginResult> with success/error info
   */
  login(credentials: LoginCredentials, returnUrl?: string): Observable<LoginResult> {
    devLog('🔐 AuthService: Starting login flow...');

    // Show splash screen
    this.showSplash();

    return this.http.post<LoginResponse>(`${this.baseUrl}/accounts/auth/login/`, credentials).pipe(
      // Step 1: Store auth data (15-30% progress)
      tap((response) => {
        devLog('✅ AuthService: Login successful, storing auth data');
        this.updateSplashProgress(15);
        this.authStore.setAuth(response);
        this.updateSplashProgress(30);
      }),

      // Step 2: Initialize session (30-70% progress)
      switchMap((response) => {
        devLog('🚀 AuthService: Initializing session (projects, templates)...');
        this.updateSplashProgress(40);

        return from(this.sessionInit.initializeSession(response)).pipe(
          tap(() => this.updateSplashProgress(70)),
          map((initResult) => ({ response, initResult }))
        );
      }),

      // Step 3: Handle initialization result and ensure correct project is active (70-85% progress)
      switchMap(({ response, initResult }) => {
        if (!initResult.success) {
          // Critical error (projects failed to load)
          devError('❌ AuthService: Session initialization failed', initResult.errors);

          // Clear auth since we can't proceed
          this.authStore.clearAuth();

          return of({
            success: false,
            error: initResult.errors[0] || 'Failed to initialize session',
            errorKey: 'LOGIN.INIT_FAILED',
            initResult
          });
        }

        this.updateSplashProgress(75);

        // Projects are loaded - now ensure correct project is active before navigating
        return from(this.ensureCorrectProjectActive(returnUrl)).pipe(
          tap(() => this.updateSplashProgress(85)),
          map(() => ({ success: true as const, initResult }))
        );
      }),

      // Step 4: Navigate after project is ready (85-100% progress)
      switchMap((result) => {
        if (!result.success) {
          // Already handled - just pass through the error
          return of({
            success: false,
            error: result.error,
            errorKey: result.errorKey
          });
        }

        this.updateSplashProgress(95);

        // Success - navigate to appropriate route
        const targetUrl = this.determineNavigationUrl(returnUrl);
        devLog(`✅ AuthService: Login complete, navigating to ${targetUrl}`);

        if (result.initResult.warnings.length > 0) {
          devWarn('⚠️ AuthService: Session initialized with warnings:', result.initResult.warnings);
        }

        // Wait for navigation to complete before hiding splash
        // This prevents the brief flash of login screen while lazy component loads
        return from(this.router.navigateByUrl(targetUrl)).pipe(
          tap(() => {
            devLog('✅ AuthService: Navigation complete');
            this.updateSplashProgress(100);
          }),
          map(() => ({
            success: true
          }))
        );
      }),

      // Step 5: Handle errors
      catchError((error: HttpErrorResponse) => {
        devError('❌ AuthService: Login failed', error);

        const result: LoginResult = {
          success: false,
          error: this.extractErrorMessage(error),
          errorKey: this.extractErrorKey(error)
        };

        return of(result);
      }),

      // Always hide splash when done (success or error)
      finalize(() => {
        devLog('🎬 AuthService: Hiding splash screen');
        this.hideSplash();
      })
    );
  }

  /**
   * Logout user
   *
   * Flow:
   * 1. Clear session data (projects, templates)
   * 2. Clear auth data
   * 3. Clear Otto tokens
   * 4. Cleanup downloads (revoke blob URLs)
   * 5. Navigate to login page
   */
  logout(returnUrl?: string): void {
    devLog('🚪 AuthService: Logging out...');

    // Clear session data (including taxonomies on logout)
    this.sessionInit.clearSession();

    // Clear auth data
    this.authStore.clearAuth();

    // Clear Otto tokens
    this.ottoService.clearCache();

    // Cleanup downloads and revoke blob URLs
    this.downloadsService.cleanup();

    // Navigate to login
    const navigationExtras = returnUrl ? { queryParams: { returnUrl } } : {};
    this.router.navigate(['/login'], navigationExtras);

    devLog('✅ AuthService: Logout complete');
  }

  /**
   * Refresh authentication token
   *
   * Called by HTTP interceptor when token expires.
   * Returns true if refresh succeeded, false otherwise.
   *
   * Note: This delegates to AuthStore.refreshToken() which handles
   * race condition prevention across multiple tabs.
   */
  async refreshToken(): Promise<boolean> {
    try {
      devLog('🔄 AuthService: Delegating token refresh to AuthStore...');
      await this.authStore.refreshToken();

      // Verify token was actually updated
      const newToken = this.authStore.token();
      if (newToken) {
        devLog('✅ AuthService: Token refresh successful - new token available');
        return true;
      }

      devWarn('⚠️ AuthService: Token refresh completed but no token available');
      return false;
    } catch (error) {
      devError('❌ AuthService: Token refresh failed', error);
      return false;
    }
  }

  /**
   * Lock session (for session expiration scenarios)
   *
   * Clears the expired token and session cache but preserves user data.
   * User will need to re-enter password to unlock.
   */
  lockSession(returnUrl?: string): void {
    devLog('🔒 AuthService: Locking session due to expired token');

    // Get current URL and clean it (remove query params to avoid duplicates)
    let url = returnUrl || this.router.url;

    // Remove existing returnUrl query param if present to avoid nesting
    url = url.split('?')[0];

    devLog(`🔒 AuthService: Return URL will be: ${url}`);

    // Clear session cache before locking (preserve taxonomies for faster unlock)
    devLog('🧹 AuthService: Clearing session cache (preserving taxonomies)');
    this.sessionInit.clearSession(); // Don't clear taxonomies on lock

    // Lock the session (this will clear the token while preserving user data)
    this.authStore.lockSessionWithReturnUrl(url);

    // Navigate to locked page with clean return URL
    this.router.navigate(['/locked'], {
      queryParams: { returnUrl: url }
    });
  }

  /**
   * Unlock session with password
   *
   * Validates password and unlocks session if correct.
   */
  unlockSession(password: string): Observable<LoginResult> {
    const user = this.authStore.user();

    if (!user) {
      devError('❌ AuthService: Cannot unlock - no user data');
      return of({
        success: false,
        error: 'No user data available',
        errorKey: 'LOGIN.NO_USER'
      });
    }

    devLog('🔓 AuthService: Attempting to unlock session');

    // Re-login with stored username and provided password
    const credentials: LoginCredentials = {
      username: user.username,
      password
    };

    return this.login(credentials, this.authStore.getReturnUrl() || undefined).pipe(
      tap((result) => {
        if (result.success) {
          devLog('✅ AuthService: Session unlocked successfully');
          this.authStore.unlockSession();
          this.authStore.broadcastUnlock(); // Notify other tabs
        }
      })
    );
  }

  // ============================================================================
  // Private Methods
  // ============================================================================

  /**
   * Ensure the correct project is active before navigating to a project-dependent URL
   *
   * Parses project ID from returnUrl (e.g., /project/480/dashboard) and sets it as active.
   * If no returnUrl and no active project, activates the first available project.
   * This ensures project-specific data (templates, etc.) are loaded before navigation.
   *
   * @param returnUrl - URL to navigate to (may contain project ID)
   */
  private async ensureCorrectProjectActive(returnUrl?: string): Promise<void> {
    // If there's a returnUrl, try to extract project ID from it
    if (returnUrl) {
      const projectId = this.extractProjectIdFromUrl(returnUrl);

      if (projectId) {
        // Check if this project exists in loaded projects
        const projects = this.sessionInit.getLoadedProjects();
        const projectExists = projects.some(p => p.id === projectId);

        if (!projectExists) {
          devWarn(`⚠️ AuthService: Project ${projectId} from URL not found in user's projects`);
          // Fall through to activate first project
        } else {
          // Set this project as active (and wait for data loading to complete)
          devLog(`📍 AuthService: Setting project ${projectId} as active before navigation`);
          await this.projectsService.setActiveProject(projectId);
          devLog(`✅ AuthService: Project ${projectId} is now active and ready`);
          return;
        }
      }
    }

    // Check if there's already an active project
    const activeProject = this.projectsService.activeProject();
    if (activeProject) {
      devLog(`📍 AuthService: Using current active project: ${activeProject.id}`);
      return;
    }

    // No returnUrl project and no active project - activate first available project
    const projects = this.sessionInit.getLoadedProjects();
    if (projects.length > 0) {
      const firstProject = projects[0];
      devLog(`📍 AuthService: No active project, activating first project: ${firstProject.id}`);
      await this.projectsService.setActiveProject(firstProject.id);
      devLog(`✅ AuthService: First project ${firstProject.id} is now active and ready`);
    } else {
      devWarn('⚠️ AuthService: No projects available for user');
    }
  }

  /**
   * Extract project ID from URL
   *
   * Handles both hash routes (/#/480/...) and regular routes (/480/...)
   *
   * @param url - URL to parse
   * @returns Project ID or null
   */
  private extractProjectIdFromUrl(url: string): number | null {
    // Remove hash if present: /#/480/481/... -> /480/481/...
    const cleanUrl = url.replace(/^#/, '');

    // Split by / and filter out empty segments
    const segments = cleanUrl.split('/').filter(s => s);

    // Expected formats:
    // - /480/481/intelligence/combo_view/377521667 (legacy)
    // - /#/480/481/intelligence/combo_view/377521667 (legacy with hash)
    // - /project/480/dashboard (new Angular routing)
    // - /#/project/480/dashboard (new with hash)

    if (segments.length > 0) {
      // Check if first segment is 'project' (new format)
      if (segments[0] === 'project' && segments.length > 1) {
        const projectId = parseInt(segments[1], 10);
        if (!isNaN(projectId) && projectId > 0) {
          devLog(`📍 AuthService: Extracted project ID ${projectId} from URL: ${url}`);
          return projectId;
        }
      } else {
        // Legacy format: first segment is the project ID
        const firstSegment = segments[0];
        const projectId = parseInt(firstSegment, 10);

        if (!isNaN(projectId) && projectId > 0) {
          devLog(`📍 AuthService: Extracted project ID ${projectId} from URL: ${url}`);
          return projectId;
        }
      }
    }

    devLog(`📍 AuthService: Could not extract project ID from URL: ${url}`);
    return null;
  }

  /**
   * Determine where to navigate after successful login
   */
  private determineNavigationUrl(returnUrl?: string): string {
    // Priority 1: Explicit returnUrl parameter
    if (returnUrl) {
      return returnUrl;
    }

    // Priority 2: Stored returnUrl from session lock
    const storedReturnUrl = this.authStore.getReturnUrl();
    if (storedReturnUrl) {
      return storedReturnUrl;
    }

    // Priority 3: Active project dashboard
    const activeProject = this.projectsService.activeProject();
    if (activeProject) {
      devLog(`📍 AuthService: Navigating to active project dashboard: ${activeProject.id}`);
      return `/project/${activeProject.id}/dashboard`;
    }

    // Priority 4: First project dashboard (if no active project)
    const projects = this.sessionInit.getLoadedProjects();
    if (projects.length > 0) {
      const firstProject = projects[0];
      devLog(`📍 AuthService: No active project, activating and navigating to first project: ${firstProject.id}`);
      this.projectsService.setActiveProject(firstProject.id);
      return `/project/${firstProject.id}/dashboard`;
    }

    // Priority 5: Fallback to root (will redirect to login if not authenticated)
    devLog('⚠️ AuthService: No projects available, redirecting to root');
    return '/';
  }

  /**
   * Extract error message from HTTP error response
   */
  private extractErrorMessage(error: HttpErrorResponse): string {
    if (error.error?.detail) {
      return error.error.detail;
    }

    if (error.error?.message) {
      return error.error.message;
    }

    if (typeof error.error === 'string') {
      return error.error;
    }

    switch (error.status) {
      case 401:
        return 'Invalid username or password';
      case 403:
        return 'Access denied';
      case 500:
        return 'Server error - please try again';
      case 0:
        return 'Network error - please check your connection';
      default:
        return 'An unexpected error occurred';
    }
  }

  /**
   * Extract i18n error key from HTTP error response
   */
  private extractErrorKey(error: HttpErrorResponse): string {
    if (error.error?.errorKey) {
      return error.error.errorKey;
    }

    switch (error.status) {
      case 401:
        return 'LOGIN.INVALID_CREDENTIALS';
      case 403:
        return 'LOGIN.ACCESS_DENIED';
      case 500:
        return 'LOGIN.SERVER_ERROR';
      case 0:
        return 'LOGIN.NETWORK_ERROR';
      default:
        return 'LOGIN.ERROR';
    }
  }

  // ============================================================================
  // Loading Splash Methods
  // ============================================================================

  /**
   * Show loading splash screen
   *
   * Creates and attaches splash component to DOM.
   */
  private showSplash(): void {
    if (this.splashComponentRef) {
      devWarn('⚠️ AuthService: Splash already showing');
      return;
    }

    devLog('🎬 AuthService: Creating splash component');

    // Create component
    this.splashComponentRef = createComponent(LoadingSplashComponent, {
      environmentInjector: this.injector
    });

    // Attach to DOM
    const hostElement = this.splashComponentRef.location.nativeElement;
    document.body.appendChild(hostElement);

    // Trigger change detection
    this.appRef.attachView(this.splashComponentRef.hostView);

    // Show splash
    this.splashComponentRef.instance.show();

    devLog('✅ AuthService: Splash component created and shown');
  }

  /**
   * Update splash progress
   *
   * @param progress - Progress percentage (0-100)
   */
  private updateSplashProgress(progress: number): void {
    if (!this.splashComponentRef) {
      return;
    }

    devLog(`📊 AuthService: Updating splash progress to ${progress}%`);
    this.splashComponentRef.instance.updateProgress(progress);
  }

  /**
   * Hide and destroy loading splash screen
   */
  private hideSplash(): void {
    if (!this.splashComponentRef) {
      return;
    }

    devLog('🎬 AuthService: Hiding splash component');

    // Hide splash (triggers fade-out animation)
    this.splashComponentRef.instance.hide();

    // Wait for fade-out animation to complete (800ms) before destroying
    setTimeout(() => {
      if (this.splashComponentRef) {
        devLog('🧹 AuthService: Destroying splash component');

        // Detach from DOM
        this.appRef.detachView(this.splashComponentRef.hostView);

        // Remove element
        const hostElement = this.splashComponentRef.location.nativeElement;
        if (hostElement.parentNode) {
          hostElement.parentNode.removeChild(hostElement);
        }

        // Destroy component
        this.splashComponentRef.destroy();
        this.splashComponentRef = null;

        devLog('✅ AuthService: Splash component destroyed');
      }
    }, 800);
  }

  /**
   * Get the current token
   * @returns The current token
   */
  getToken(): string {
    const token = this.authStore.token();
    if (!token) {
      throw new Error('No token available');
    }
    return token;
  }
}

